import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AmbientBackground } from "@/components/AmbientBackground";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sparkles, Mail, Lock, ArrowRight } from "lucide-react";
import { formatApiError } from "@/lib/api";

const DEMO_ACCOUNTS = [
  { role: "Admin", email: "admin@sopflow.io", password: "admin123" },
  { role: "Champion", email: "champion@sopflow.io", password: "demo1234" },
  { role: "Owner", email: "owner@sopflow.io", password: "demo1234" },
  { role: "Reader", email: "user@sopflow.io", password: "demo1234" },
];

export default function Login() {
  const [email, setEmail] = useState("admin@sopflow.io");
  const [password, setPassword] = useState("admin123");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, microsoftLogin } = useAuth();

  const from = location.state?.from?.pathname || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const ssoLogin = async () => {
    setErr("");
    setBusy(true);
    try {
      await microsoftLogin(email);
      navigate(from, { replace: true });
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Microsoft SSO isn't configured yet — using stubbed flow.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-10">
      <AmbientBackground />
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mb-4 inline-flex items-center gap-1.5 text-xs text-[#9AA4B2] hover:text-white transition-colors"
          >
            ← Back to Home
          </button>
          <br />
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-xs uppercase tracking-widest text-cyan-300 mb-6" data-testid="login-brand-chip">
            <Sparkles size={12} /> SOPFlow · Standard Operating Procedures
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white leading-[1.05] tracking-tight">
            Every SOP.<br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#7DD3FC] via-[#818CF8] to-[#C4B5FD]">
              One source of truth.
            </span>
          </h1>
          <p className="mt-3 text-[#9AA4B2] text-sm">Sign in to access your organization&apos;s procedures library.</p>
        </div>

        <form
          onSubmit={submit}
          className="glass-strong rounded-3xl p-6 md:p-8 space-y-4"
          data-testid="login-form"
        >
          <div>
            <label className="text-xs uppercase tracking-widest text-[#9AA4B2]">Work email</label>
            <div className="relative mt-1.5">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5B6472]" size={16} />
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="bg-white/[0.04] border-white/10 rounded-full pl-11 py-6 text-white placeholder:text-[#5B6472] focus:border-cyan-400/60"
                required
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#9AA4B2]">Password</label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5B6472]" size={16} />
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-white/[0.04] border-white/10 rounded-full pl-11 py-6 text-white placeholder:text-[#5B6472] focus:border-cyan-400/60"
                required
              />
            </div>
          </div>

          {err && (
            <div className="text-sm text-rose-400" data-testid="login-error">
              {err}
            </div>
          )}

          <Button
            type="submit"
            data-testid="login-submit-btn"
            disabled={busy}
            className="w-full btn-primary-glow rounded-full py-6 text-base font-medium"
          >
            {busy ? "Signing in…" : (<span className="flex items-center justify-center gap-2">Sign in <ArrowRight size={16} /></span>)}
          </Button>

          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] uppercase tracking-widest text-[#5B6472]">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <button
            type="button"
            onClick={ssoLogin}
            disabled={busy}
            data-testid="login-microsoft-btn"
            className="w-full btn-ghost-glass rounded-full py-3.5 flex items-center justify-center gap-2 text-sm font-medium"
          >
            <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#F25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            Sign in with Microsoft
            <span className="ml-1 text-[10px] text-[#5B6472]">(stubbed)</span>
          </button>
        </form>

        <div className="mt-4 glass rounded-3xl p-4">
          <div className="text-xs uppercase tracking-widest text-[#9AA4B2] mb-2">Demo accounts · one-click</div>
          <div className="grid grid-cols-2 gap-2">
            {DEMO_ACCOUNTS.map((a) => (
              <button
                key={a.email}
                type="button"
                onClick={() => {
                  setEmail(a.email);
                  setPassword(a.password);
                }}
                className="text-left px-3 py-2 rounded-2xl bg-white/[0.03] hover:bg-white/[0.07] border border-white/5 transition"
                data-testid={`demo-account-${a.role.toLowerCase()}`}
              >
                <div className="text-sm text-white">{a.role}</div>
                <div className="text-[10px] text-[#5B6472] truncate">{a.email}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
